/**
 * Event map — Leaflet view of the wells on a sampling event's plan,
 * the site polygon, and the tech's current GPS position. Tiles are
 * served from cached_map_tiles when available (offline-capable), else
 * from OpenStreetMap online. When fully offline with no cached tiles,
 * the map degrades to well markers + polygon + position on a dark
 * canvas — still useful for "which dot am I near?".
 *
 * NOTE: full MBTiles offline-pack ingestion (parse SQLite client-side,
 * populate cached_map_tiles) is a follow-up — it needs sql.js (~1 MB
 * WASM) which we don't want in the critical path yet. The read path
 * here is ready; the write path (download + extract) ships later.
 */
import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getDb } from '../db';
import type { SitePolygon } from '../hooks/useSiteGeofence';

// Leaflet's default marker icons reference asset paths that break under
// Vite's bundler. We use lightweight div-icons instead, so no fixups
// are needed — but if a future change reintroduces image markers,
// remember to patch L.Icon.Default.

export interface MapWell {
  well_id: string;
  well_name: string;
  lat: number;
  lon: number;
  status: 'planned' | 'draft' | 'complete' | 'flagged';
}

export interface EventMapProps {
  /** Site polygon for the outline (GeoJSON). Optional. */
  polygon?: SitePolygon | null;
  /** Site centroid; used to center the map when no wells have coords. */
  centroid?: { lat: number; lon: number } | null;
  wells: MapWell[];
  /** Current device GPS fix, if any. */
  position?: { lat: number; lon: number; accuracy_m: number } | null;
  /** Called when a well marker is tapped. */
  onWellTap?: (well_id: string) => void;
  className?: string;
}

const STATUS_COLOR: Record<MapWell['status'], string> = {
  planned: '#64748b', // mono-500
  draft: '#f59e0b', // amber
  complete: '#22c55e', // green
  flagged: '#ef4444', // red
};

function wellDivIcon(status: MapWell['status']): L.DivIcon {
  const color = STATUS_COLOR[status];
  return L.divIcon({
    className: '',
    html: `<span style="
      display:block;width:14px;height:14px;border-radius:50%;
      background:${color};border:2px solid #0b1220;
      box-shadow:0 0 0 1px rgba(255,255,255,0.25);"></span>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

function positionDivIcon(): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<span style="
      display:block;width:16px;height:16px;border-radius:50%;
      background:#3b82f6;border:3px solid #fff;
      box-shadow:0 0 12px rgba(59,130,246,0.8);"></span>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

/** Custom GridLayer that serves PNG tiles from cached_map_tiles when
 *  present, else falls back to the standard online URL. */
function makeCachedTileLayer(style: string): L.GridLayer {
  const fallbackUrl = (z: number, x: number, y: number) =>
    `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;

  const Layer = L.GridLayer.extend({
    createTile(coords: L.Coords, done: L.DoneCallback) {
      const tile = document.createElement('img');
      tile.alt = '';
      const key = `${coords.z}|${coords.x}|${coords.y}|${style}`;
      void getDb()
        .cached_map_tiles.get(key)
        .then((row) => {
          if (row?.bytes) {
            const url = URL.createObjectURL(row.bytes);
            tile.onload = () => {
              URL.revokeObjectURL(url);
              done(undefined, tile);
            };
            tile.onerror = () => {
              URL.revokeObjectURL(url);
              // Fall through to online.
              loadOnline();
            };
            tile.src = url;
          } else {
            loadOnline();
          }
        })
        .catch(() => loadOnline());

      function loadOnline() {
        tile.onload = () => done(undefined, tile);
        tile.onerror = () => done(new Error('tile load failed'), tile);
        // Will fail silently when offline → blank tile, dots still show.
        tile.src = fallbackUrl(coords.z, coords.x, coords.y);
      }
      return tile;
    },
  });
  return new (Layer as unknown as new (opts?: L.GridLayerOptions) => L.GridLayer)({
    minZoom: 3,
    maxZoom: 19,
    attribution: '© OpenStreetMap contributors',
  });
}

export function EventMap({
  polygon,
  centroid,
  wells,
  position,
  onWellTap,
  className,
}: EventMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const overlayRef = useRef<L.LayerGroup | null>(null);
  const posMarkerRef = useRef<L.Marker | null>(null);
  const posAccCircleRef = useRef<L.Circle | null>(null);

  // Init map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      zoomControl: true,
      attributionControl: true,
    });
    makeCachedTileLayer('osm').addTo(map);
    mapRef.current = map;
    overlayRef.current = L.layerGroup().addTo(map);

    // Initial view: fit to wells with coords, else centroid, else world.
    const withCoords = wells.filter(
      (w) => Number.isFinite(w.lat) && Number.isFinite(w.lon),
    );
    if (withCoords.length > 0) {
      const bounds = L.latLngBounds(withCoords.map((w) => [w.lat, w.lon]));
      map.fitBounds(bounds.pad(0.2), { maxZoom: 18 });
    } else if (centroid) {
      map.setView([centroid.lat, centroid.lon], 16);
    } else {
      map.setView([39, -98], 4); // continental US
    }

    return () => {
      map.remove();
      mapRef.current = null;
      overlayRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redraw overlay (polygon + well markers) whenever inputs change.
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    overlay.clearLayers();

    if (polygon && polygon.coordinates?.[0]?.length) {
      // GeoJSON is [lng, lat]; Leaflet wants [lat, lng].
      const ring = polygon.coordinates[0].map(([lng, lat]) => [lat, lng] as [number, number]);
      L.polygon(ring, {
        color: '#94a3b8',
        weight: 1.5,
        fillOpacity: 0.05,
      }).addTo(overlay);
    }

    for (const w of wells) {
      if (!Number.isFinite(w.lat) || !Number.isFinite(w.lon)) continue;
      const m = L.marker([w.lat, w.lon], { icon: wellDivIcon(w.status) }).addTo(
        overlay,
      );
      m.bindTooltip(w.well_name, { direction: 'top', offset: [0, -8] });
      if (onWellTap) m.on('click', () => onWellTap(w.well_id));
    }
  }, [polygon, wells, onWellTap]);

  // Update the GPS position marker.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!position) {
      posMarkerRef.current?.remove();
      posAccCircleRef.current?.remove();
      posMarkerRef.current = null;
      posAccCircleRef.current = null;
      return;
    }
    const ll: L.LatLngExpression = [position.lat, position.lon];
    if (posMarkerRef.current) {
      posMarkerRef.current.setLatLng(ll);
    } else {
      posMarkerRef.current = L.marker(ll, { icon: positionDivIcon() }).addTo(map);
    }
    if (posAccCircleRef.current) {
      posAccCircleRef.current.setLatLng(ll).setRadius(position.accuracy_m);
    } else {
      posAccCircleRef.current = L.circle(ll, {
        radius: position.accuracy_m,
        color: '#3b82f6',
        weight: 1,
        fillOpacity: 0.08,
      }).addTo(map);
    }
  }, [position]);

  return (
    <div
      ref={containerRef}
      className={className ?? 'w-full h-64 rounded-md overflow-hidden border border-mono-800'}
      // Leaflet needs an explicit height; the className handles it but
      // belt-and-suspenders for the default case.
      style={{ minHeight: '16rem', background: '#0b1220' }}
    />
  );
}
